import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled as styled2 } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Field } from '@atlaskit/form';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock-circle';
import ChevronDown from '@atlaskit/icon/utility/migration/chevron-down';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { useSafeNotificationsSettingWithInitialisation } from '@atlassian/jira-products-jsm-compliance-main/src/services/safe-notifications-setting';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import {
	ReactCodeMirror,
	type ReactCodeMirrorElement,
} from '@atlassian/jira-servicedesk-customer-notification-settings-templates/src/ui/code-mirror-wrapper';
import type {
	DisplayLabel,
	TranslationLogicalId,
} from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/services/get-data/types.tsx';
import type { TemplateVariable } from '../../common/types';
import {
	useFieldState,
	useFormDirty,
	useManageTranslationsActions,
} from '../../controllers/manage-translations/index.tsx';
import { useTemplateVariables } from '../../services/template-variables/index.tsx';
import messages from './messages';
import type { EmailTemplateProps, TemplateType } from './types';

const logicalIds = {
	emailSubject: 'sd.notifications.custom.email.subject',
	plainTextTemplate: 'sd.notifications.custom.email.plaintext.template',
	htmlTemplate: 'sd.notifications.custom.email.html.template',
	templateCss: 'sd.notifications.custom.email.template.css',
};

export const EmailTemplatePanel = ({
	defaultLanguage,
	selectedLanguage,
	translationPanel,
	projectId,
	projectKey,
	ContentPreview,
	SendMeATestEmail,
}: EmailTemplateProps) => {
	const { translationFields } = translationPanel.translationGroups[0];
	const { editDisabled: isReadOnly } = translationPanel;

	const { updateFields } = useManageTranslationsActions();

	const findField = (logicalId: TranslationLogicalId) =>
		translationFields.find((field) => field.logicalId === logicalId);

	const findContent = (logicalId: TranslationLogicalId, isDefault: boolean): DisplayLabel =>
		translationFields.find((translationField) => translationField.logicalId === logicalId)
			?.translationContentMap[isDefault ? defaultLanguage.key : selectedLanguage.key] ?? '';

	const { formatMessage } = useIntl();
	const { isSafeNotificationsEnabled } = useSafeNotificationsSettingWithInitialisation();
	const { data: templateVariableData, fetch: templateVariableFetch } =
		useTemplateVariables(projectId);

	const handleEmailSubjectChange: React.FormEventHandler<HTMLInputElement> = (e) => {
		updateFields({
			fieldContent: e.currentTarget.value,
			fieldLogicalId: logicalIds.emailSubject,
		});
	};

	const handleCodeMirrorFieldChange = (logicalId: string) => (content: string) => {
		updateFields({ fieldContent: content, fieldLogicalId: logicalId });
	};

	const shouldShowFlag = (logicalId: string) =>
		findField(logicalId)?.translationState !== 'up_to_date';

	const emailSubject = findContent(logicalIds.emailSubject, false);
	const plainTextTemplate = findContent(logicalIds.plainTextTemplate, false);
	const htmlTemplate = findContent(logicalIds.htmlTemplate, false);
	const css = findContent(logicalIds.templateCss, false);

	const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
	const isDirty = useFormDirty();
	const fieldState = useFieldState(logicalIds.emailSubject);

	const emailSubjectRef = useRef<HTMLInputElement>(null);
	const htmlCodeMirrorRef = useRef<ReactCodeMirrorElement>(null);
	const cssCodeMirrorRef = useRef<ReactCodeMirrorElement>(null);
	const plainCodeMirrorRef = useRef<ReactCodeMirrorElement>(null);
	const codeMirrorRefs = useMemo(
		() => ({ html: htmlCodeMirrorRef, css: cssCodeMirrorRef, text: plainCodeMirrorRef }),
		[],
	);
	const hasPrivacyConcernIcon = useCallback(
		(variable: TemplateVariable) => isSafeNotificationsEnabled && !!variable.privacyConcerns.length,
		[isSafeNotificationsEnabled],
	);

	useEffect(() => {
		templateVariableFetch();
	}, [templateVariableFetch]);

	// Set the input field according to server-provided values
	useEffect(() => {
		if (emailSubjectRef.current) {
			emailSubjectRef.current.value = emailSubject;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Reset to server-provided values if discard is clicked, i.e. the state value for email subject has changed but the field is not dirty and the field is in unchanged state which means that the field matches the server-provided value
	useEffect(() => {
		if (emailSubjectRef.current && !isDirty && fieldState?.changeType === 'unchanged') {
			emailSubjectRef.current.value = emailSubject;
		}
	}, [emailSubject, fieldState?.changeType, isDirty, templateVariableData]);

	const onEmailSubjectVariableSelect = (variableKey: string) => () => {
		const insert = `\${${variableKey}}`;
		if (emailSubjectRef.current instanceof HTMLInputElement) {
			const inputRef = emailSubjectRef.current;
			// @ts-expect-error - TS2345 - Argument of type 'number | null' is not assignable to parameter of type 'number'.
			inputRef.setRangeText(insert, inputRef.selectionStart, inputRef.selectionEnd, 'end');
			inputRef.focus();

			updateFields({ fieldContent: inputRef.value, fieldLogicalId: logicalIds.emailSubject });
		} else {
			updateFields({ fieldContent: insert, fieldLogicalId: logicalIds.emailSubject });
		}
	};
	const onEmailTemplateVariableSelect = useCallback(
		(variableKey: string, templateType: TemplateType) => () => {
			const insert = `\${${variableKey}}`;
			codeMirrorRefs[templateType]?.current?.replaceSelection(insert);
			codeMirrorRefs[templateType]?.current?.focus();
		},
		[codeMirrorRefs],
	);

	const [isHtmlModalOpen, setIsHtmlModalOpen] = useState(false);
	const [isPlainTextModalOpen, setIsPlainTextModalOpen] = useState(false);

	const onHtmlEmailLabelClick = () => {
		setIsHtmlModalOpen(true);
	};

	const onPlainTextEmailLabelClick = () => {
		setIsPlainTextModalOpen(true);
	};

	const closeHtmlModal = () => {
		setIsHtmlModalOpen(false);
	};

	const closePlainTextModal = () => {
		setIsPlainTextModalOpen(false);
	};

	const [isSendTestEmailDropDownOpen, setIsSendTestEmailDropDownOpen] = useState(false);

	const TemplateDropdown = useCallback(
		({ templateType }: { templateType: TemplateType }) => {
			if (isReadOnly) {
				return null;
			}
			return (
				<TemplateVariableDropdownWrapper>
					<DropdownMenu
						trigger={({ triggerRef, ...dropdownProps }) => (
							<Button {...dropdownProps} appearance="link" ref={triggerRef}>
								{formatMessage(messages.insertVariableTrigger)}
							</Button>
						)}
					>
						{templateVariableData?.stylingVariables.map((variableGroup, groupIndex) => (
							<DropdownItemGroup key={groupIndex}>
								{variableGroup.variables.map((variable, idx) => (
									<DropdownItem
										key={idx}
										onClick={onEmailTemplateVariableSelect(variable.key, templateType)}
									>
										<ItemWrapper>
											{variable.label}
											{hasPrivacyConcernIcon(variable) && (
												<ToolTip
													content={formatMessage(messages.hiddenFromEmailContent)}
													position="right"
												>
													<LockLockedIcon label="" color={token('color.icon')} />
												</ToolTip>
											)}
										</ItemWrapper>
									</DropdownItem>
								))}
							</DropdownItemGroup>
						))}
					</DropdownMenu>
				</TemplateVariableDropdownWrapper>
			);
		},
		[
			hasPrivacyConcernIcon,
			formatMessage,
			isReadOnly,
			onEmailTemplateVariableSelect,
			templateVariableData?.stylingVariables,
		],
	);

	return (
		<TabWrapper>
			<Tabs id="email-template-styles" onChange={setActiveTabIndex} selected={activeTabIndex}>
				<TabList>
					<Tab testId="language-settings.ui.email-template-panel.translation-panel">
						{formatMessage(messages.translationTab)}
					</Tab>
					<Tab testId="language-settings.ui.email-template-panel.project-default-panel">
						{formatMessage(messages.translationDefaultTab)}
					</Tab>
				</TabList>
				<TabPanel>
					<LanguageTitleWrapper>
						<LanguageTitle>{selectedLanguage.label}</LanguageTitle>
						<ActionWrapper>
							<SendTestEmailDropDownWrapper>
								<DropdownMenu
									defaultOpen={false}
									testId="language-settings.ui.email-template-panel.send-test-email-dropdown"
									isOpen={isSendTestEmailDropDownOpen}
									onOpenChange={({ isOpen }) => setIsSendTestEmailDropDownOpen(isOpen)}
									trigger={({ triggerRef, ...dropdownProps }) => (
										<Button
											{...dropdownProps}
											ref={triggerRef}
											iconAfter={
												<ChevronDown label="" LEGACY_size="medium" color={token('color.icon')} />
											}
										>
											{formatMessage(messages.sendTestEmailLabel)}
										</Button>
									)}
								>
									<DropdownItemGroup>
										<SendMeATestEmailWrapper>
											<SendMeATestEmail
												format="html"
												projectKey={projectKey}
												initialData={{
													languageTag: selectedLanguage.key,
													contexts: [],
												}}
												stylingData={{
													css,
													htmlTemplate,
													subjectTemplate: emailSubject,
													textTemplate: plainTextTemplate,
												}}
												setIsSendTestEmailDropDownOpen={setIsSendTestEmailDropDownOpen}
											/>
											<SendMeATestEmail
												format="plain_text"
												projectKey={projectKey}
												initialData={{
													languageTag: selectedLanguage.key,
													contexts: [],
												}}
												stylingData={{
													css,
													htmlTemplate,
													subjectTemplate: emailSubject,
													textTemplate: plainTextTemplate,
												}}
												setIsSendTestEmailDropDownOpen={setIsSendTestEmailDropDownOpen}
											/>
										</SendMeATestEmailWrapper>
									</DropdownItemGroup>
								</DropdownMenu>
							</SendTestEmailDropDownWrapper>
							<PreviewDropDownWrapper>
								<DropdownMenu
									defaultOpen={false}
									testId="language-settings.ui.email-template-panel.preview-dropdown"
									trigger={({ triggerRef, ...dropdownProps }) => (
										<Button
											{...dropdownProps}
											ref={triggerRef}
											iconAfter={
												<ChevronDown label="" LEGACY_size="medium" color={token('color.icon')} />
											}
										>
											{formatMessage(messages.previewLabel)}
										</Button>
									)}
								>
									<DropdownItemGroup>
										<DropdownItem onClick={onHtmlEmailLabelClick}>
											{formatMessage(messages.htmlEmailLabel)}
										</DropdownItem>
										<DropdownItem onClick={onPlainTextEmailLabelClick}>
											{formatMessage(messages.plainTextEmailLabel)}
										</DropdownItem>
									</DropdownItemGroup>
								</DropdownMenu>
							</PreviewDropDownWrapper>
						</ActionWrapper>
					</LanguageTitleWrapper>
					<Form>
						<Field label={formatMessage(messages.subjectLabel)} name="subject">
							{() => (
								<SubjectWrapper showFlag={shouldShowFlag(logicalIds.emailSubject)}>
									<Textfield
										aria-label={formatMessage(messages.subjectLabel)}
										ref={emailSubjectRef}
										isReadOnly={isReadOnly}
										isDisabled={isReadOnly}
										onChange={handleEmailSubjectChange}
									/>
									{!isReadOnly && (
										<SubjectVariableDropdownWrapper>
											<DropdownMenu
												trigger={({ triggerRef, ...dropdownProps }) => (
													<Button {...dropdownProps} appearance="link" ref={triggerRef}>
														{formatMessage(messages.insertVariableTrigger)}
													</Button>
												)}
											>
												{templateVariableData?.subjectVariables.map((variable, idx) => (
													<DropdownItem
														key={idx}
														onClick={onEmailSubjectVariableSelect(variable.key)}
													>
														<SubjectItemWrapper>
															{variable.label}
															{isSafeNotificationsEnabled && !!variable.privacyConcerns.length && (
																<ToolTip
																	content={formatMessage(messages.hiddenFromEmailContent)}
																	position="right"
																>
																	<LockLockedIcon label="" color={token('color.icon')} />
																</ToolTip>
															)}
														</SubjectItemWrapper>
													</DropdownItem>
												))}
											</DropdownMenu>
										</SubjectVariableDropdownWrapper>
									)}
								</SubjectWrapper>
							)}
						</Field>

						<h5>{formatMessage(messages.stylesTitle)}</h5>
						<JsmPageInteractiveEvent />
						<TemplateWrapper showFlag={shouldShowFlag(logicalIds.htmlTemplate)}>
							<SubTitle>{formatMessage(messages.htmlSubtitle)}</SubTitle>
							<TemplateDropdown templateType="html" />

							<ReactCodeMirror
								ref={htmlCodeMirrorRef}
								value={htmlTemplate}
								mode="html"
								isReadOnly={isReadOnly}
								onChange={handleCodeMirrorFieldChange(logicalIds.htmlTemplate)}
							/>
						</TemplateWrapper>
						<TemplateWrapper showFlag={shouldShowFlag(logicalIds.templateCss)}>
							<SubTitle>{formatMessage(messages.cssSubtitle)}</SubTitle>
							<ReactCodeMirror
								ref={cssCodeMirrorRef}
								value={css}
								mode="css"
								isReadOnly={isReadOnly}
								onChange={handleCodeMirrorFieldChange(logicalIds.templateCss)}
							/>
						</TemplateWrapper>
						<TemplateWrapper showFlag={shouldShowFlag(logicalIds.plainTextTemplate)}>
							<SubTitle>{formatMessage(messages.textSubtitle)}</SubTitle>
							<TemplateDropdown templateType="text" />
							<ReactCodeMirror
								ref={plainCodeMirrorRef}
								value={plainTextTemplate}
								mode="plain"
								isReadOnly={isReadOnly}
								onChange={handleCodeMirrorFieldChange(logicalIds.plainTextTemplate)}
							/>
						</TemplateWrapper>
					</Form>
				</TabPanel>
				<TabPanel>
					<LanguageTitleWrapper>
						<LanguageTitle>{defaultLanguage.label}</LanguageTitle>
					</LanguageTitleWrapper>
					<Form>
						<Field label={formatMessage(messages.subjectLabel)} name="subject">
							{() => (
								<SubjectWrapper>
									<Textfield
										ref={emailSubjectRef}
										isReadOnly
										isDisabled
										value={findContent(logicalIds.emailSubject, true)}
									/>
								</SubjectWrapper>
							)}
						</Field>

						<h5>{formatMessage(messages.stylesTitle)}</h5>

						<TemplateWrapper>
							<SubTitle>{formatMessage(messages.htmlSubtitle)}</SubTitle>
							<ReactCodeMirror
								ref={htmlCodeMirrorRef}
								value={findContent(logicalIds.htmlTemplate, true)}
								mode="html"
								isReadOnly
								// Replace with lodash/noop
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onChange={() => {}}
							/>
						</TemplateWrapper>
						<TemplateWrapper>
							<SubTitle>{formatMessage(messages.cssSubtitle)}</SubTitle>
							<ReactCodeMirror
								ref={cssCodeMirrorRef}
								value={findContent(logicalIds.templateCss, true)}
								mode="css"
								isReadOnly
								// Replace with lodash/noop
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onChange={() => {}}
							/>
						</TemplateWrapper>
						<TemplateWrapper>
							<SubTitle>{formatMessage(messages.textSubtitle)}</SubTitle>
							<ReactCodeMirror
								ref={plainCodeMirrorRef}
								value={findContent(logicalIds.plainTextTemplate, true)}
								mode="plain"
								isReadOnly
								// Replace with lodash/noop
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onChange={() => {}}
							/>
						</TemplateWrapper>
					</Form>
				</TabPanel>
			</Tabs>
			{isHtmlModalOpen && (
				<ModalTransition>
					<Modal onClose={closeHtmlModal}>
						<ModalHeader>
							<ModalTitle>{formatMessage(messages.emailTemplatePreviewModalTitle)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<ContentPreview
								content={{
									css,
									htmlTemplate,
									subjectTemplate: emailSubject,
									textTemplate: plainTextTemplate,
								}}
								initialData={{
									contexts: [],
									languageTag: selectedLanguage.key,
									format: 'html',
								}}
								projectKey={projectKey}
							/>
						</ModalBody>
					</Modal>
				</ModalTransition>
			)}
			{isPlainTextModalOpen && (
				<ModalTransition>
					<Modal onClose={closePlainTextModal}>
						<ModalHeader>
							<ModalTitle>{formatMessage(messages.emailTemplatePreviewModalTitle)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<ContentPreview
								content={{
									css,
									htmlTemplate,
									subjectTemplate: emailSubject,
									textTemplate: plainTextTemplate,
								}}
								initialData={{
									contexts: [],
									languageTag: selectedLanguage.key,
									format: 'plain_text',
								}}
								projectKey={projectKey}
							/>
						</ModalBody>
					</Modal>
				</ModalTransition>
			)}
		</TabWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabWrapper = styled2.div({
	position: 'relative',
	paddingInline: token('space.100', '8px'),
	paddingBottom: token('space.200', '16px'),
	maxWidth: '960px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& > div > [role='tabpanel']": {
		flexDirection: 'column',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"& > div > [role='tablist']": {
		borderBottom: `1px solid ${token('color.border', '#EAEAEA')}`,
		'&::before': {
			display: 'none',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Form = styled2.form({
	position: 'relative',
	maxWidth: '960px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > div:first-child': {
		maxWidth: '600px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LanguageTitle = styled2.header({
	font: token('font.body.large'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionWrapper = styled2.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button': {
		marginRight: token('space.100', '8px'),
	},
	marginBottom: token('space.500', '40px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SendMeATestEmailWrapper = styled2.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SendTestEmailDropDownWrapper = styled2.div({
	position: 'absolute',
	right: token('space.1000', '80px'),
	paddingRight: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreviewDropDownWrapper = styled2.div({
	position: 'absolute',
	right: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LanguageTitleWrapper = styled2.div({
	display: 'flex',
	justifyContent: 'space-between',
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubjectWrapper = styled2.div<{ showFlag?: boolean }>(
	{
		position: 'relative',
		marginBottom: token('space.400', '32px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ showFlag }) =>
		showFlag
			? `& [data-ds--text-field--container]:not(:focus-within) {
            border-left: 5px solid ${token('color.border.warning', 'colors.Y200')};
            transition: border-left 0.05s ease-in;
            }`
			: undefined,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TemplateWrapper = styled2.div<{ showFlag?: boolean }>(
	{
		position: 'relative',
		marginTop: token('space.300', '24px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ showFlag }) =>
		showFlag
			? `& .cm-editor:not(:focus-within) {
                border-left: 5px solid ${token('color.border.warning', 'colors.Y200')};
                transition: border-left 0.05s ease-in;
            }`
			: undefined,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubjectVariableDropdownWrapper = styled2.div({
	position: 'absolute',
	right: 0,
	top: '-36px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubTitle = styled2.h6({
	marginBottom: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubjectItemWrapper = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		marginLeft: token('space.100', '8px'),
	},
	display: 'flex',
	alignItems: 'center',
	margin: `0 ${token('space.100', '8px')}`,
	width: '128px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TemplateVariableDropdownWrapper = styled2.div({
	position: 'absolute',
	top: token('space.negative.100', '-8px'),
	right: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemWrapper = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		marginLeft: token('space.100', '8px'),
	},
	display: 'flex',
	alignItems: 'center',
	margin: `0 ${token('space.100', '8px')}`,
});
